import React, {useState, useCallback, useMemo} from 'react';
import {observer} from 'mobx-react';
import {FormattedMessage} from 'react-intl';
import _ from 'lodash';
import RowViewModel from '../../state/RowViewModel';
import RemoveModal from 'components/modals/RemoveModal';
import TableOverlay from 'components/tables/components/TableOverlay';
import AccordionTableHeaders from './AccordionTableHeaders';
import AccordionTableRow from './AccordionTableRow';

const AccordionTableContent = observer(({
  models,
  folders,
  isUpdating,
  isFoldersUpdating,
  columns,
  emptyStateMessage,
  onRemove,
  modelsUpdated,
  localization,
  folderColumns,
  expandedFolderIds = [],
  onFolderExpanded,
  onFolderCollapsed,
  onExpand,
  filter,
  ...rest
}) => {
  const {horizontalScroll} = rest;
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [modelBeingRemoved, setModelBeingRemoved] = useState(null);

  const openRemoveModal = useCallback((model) => {
    setModelBeingRemoved(model);
    setRemoveModalOpen(true);
  }, []);

  const cancelRemove = useCallback(() => {
    setModelBeingRemoved(null);
    setRemoveModalOpen(false);
  }, []);

  const confirmRemove = useCallback(async () => {
    await onRemove(modelBeingRemoved);
    if (modelsUpdated) {
      await modelsUpdated();
    }
    setModelBeingRemoved(null);
    setRemoveModalOpen(false);
  }, [onRemove, modelBeingRemoved, modelsUpdated]);

  const wrappedFolders = useMemo(() => {
    return (folders || []).map(folder => {
      const rowViewModel = new RowViewModel(folder);
      rowViewModel.isFolder = true;
      if (expandedFolderIds.includes(folder.id)) {
        onExpand(rowViewModel.data, filter);
        rowViewModel.isExpanded = true;
      }
      return rowViewModel;
    })},
    [folders]
  );

  const wrappedModels = useMemo(() =>
    models.map(model => new RowViewModel(model)),
    [models]
  );

  const {removeModal} = localization;

  if (_.isEmpty(models) && _.isEmpty(folders)) {
    return (
      <div className='center jumbo rounded h5 py2 px1 Table-emptyState'>
        <FormattedMessage id={emptyStateMessage || 'No items to display'}/>
      </div>
    );
  }

  return (
    <div className={`AccordionTable Table ${horizontalScroll ? 'overflow-auto' : ''}`}>
      <TableOverlay isUpdating={isUpdating || isFoldersUpdating}/>
      <div className={horizontalScroll ? 'width-auto min-width-full' : 'full-width'}>
        <AccordionTableHeaders columns={columns} horizontalScroll={horizontalScroll} {...rest} />
      </div>
      <div className={horizontalScroll ? 'width-auto min-width-full' : 'full-width'}>
        {wrappedFolders.map((viewModel, index) => (
          <AccordionTableRow
            key={`folder-${viewModel.data.id || index}`}
            viewModel={viewModel}
            level={0}
            columns={columns}
            folderColumns={folderColumns}
            localization={localization}
            onRemove={onRemove}
            openRemoveModal={openRemoveModal}
            onFolderExpanded={onFolderExpanded}
            onFolderCollapsed={onFolderCollapsed}
            filter={filter}
            onExpand={onExpand}
            {...rest}
          />
        ))}
        {wrappedModels.map((viewModel, index) => (
          <AccordionTableRow
            key={viewModel.data.id || index}
            viewModel={viewModel}
            level={0}
            columns={columns}
            localization={localization}
            openRemoveModal={openRemoveModal}
            onRemove={onRemove}
            filter={filter}
            onExpand={onExpand}
            {...rest}
          />
        ))}
      </div>
      <RemoveModal
        isOpen={removeModalOpen}
        onHide={cancelRemove}
        onConfirm={confirmRemove}
        {...removeModal}
      />
    </div>
  );
});

export default AccordionTableContent;
